import type {
	VisionGpuStatus,
	VisionPlan,
	VisionRecipe,
	VisionRun,
	VisionRunBoxes,
	VisionRunView,
} from "@sitescene/types";

import { VisionGpuState, VisionRunStatus } from "@sitescene/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";

export interface StartRunInput {
	instruction: string;
	plan: VisionPlan;
	recipeId?: string;
	videoId: string;
}

const RUN_POLL_MS = 3000;

/** Still with the GPU worker or Gemini. */
export const isRunOpen = (run: Pick<VisionRun, "status">): boolean =>
	run.status !== VisionRunStatus.Completed &&
	run.status !== VisionRunStatus.Failed;

/**
 * The GPU worker's state. Polled quickly while it's starting, so the chip
 * turns ready soon after it does, and slowly otherwise.
 */
export const useVisionGpu = () =>
	useQuery({
		queryFn: async () => {
			const response = await apiClient.get<VisionGpuStatus>(
				ENDPOINTS.shadow.vision.gpu,
			);
			return response.data;
		},
		queryKey: queryKeys.vision.gpu,
		refetchInterval: (query) =>
			query.state.data?.state === VisionGpuState.Starting ? 5000 : 30_000,
	});

/** Start the GPU ahead of a run. */
export const useWarmGpu = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			const response = await apiClient.post<VisionGpuStatus>(
				ENDPOINTS.shadow.vision.gpuWarm,
			);
			return response.data;
		},
		// The worker takes a minute to be asked for; show starting until then
		onSuccess: (status) =>
			queryClient.setQueryData<VisionGpuStatus>(queryKeys.vision.gpu, {
				...status,
				state:
					status.state === VisionGpuState.Off
						? VisionGpuState.Starting
						: status.state,
			}),
	});
};

/** Gemini's plan for an instruction. Nothing is saved. */
export const usePlanInstruction = () =>
	useMutation({
		mutationFn: async (input: { instruction: string; videoId: string }) => {
			const response = await apiClient.post<VisionPlan>(
				ENDPOINTS.shadow.vision.plan,
				input,
			);
			return response.data;
		},
	});

export const useStartRun = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (input: StartRunInput) => {
			const response = await apiClient.post<VisionRun>(
				ENDPOINTS.shadow.vision.runs,
				input,
			);
			return response.data;
		},
		onSuccess: (run) =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: queryKeys.vision.runs(run.video),
				}),
				queryClient.invalidateQueries({ queryKey: queryKeys.vision.gpu }),
			]),
	});
};

/** A video's runs, newest first. */
export const useVisionRuns = (videoId: string | undefined) =>
	useQuery({
		enabled: Boolean(videoId),
		queryFn: async () => {
			const response = await apiClient.get<VisionRun[]>(
				ENDPOINTS.shadow.vision.runs,
				{ params: { videoId } },
			);
			return response.data;
		},
		queryKey: queryKeys.vision.runs(videoId ?? ""),
	});

/** One run, polled until it finishes; the run list refreshes when it does. */
export const useVisionRun = (runId: string | undefined) => {
	const queryClient = useQueryClient();
	return useQuery({
		enabled: Boolean(runId),
		queryFn: async () => {
			const response = await apiClient.get<VisionRunView>(
				ENDPOINTS.shadow.vision.run(runId ?? ""),
			);
			if (!isRunOpen(response.data)) {
				void queryClient.invalidateQueries({
					queryKey: queryKeys.vision.runs(response.data.video),
				});
			}
			return response.data;
		},
		queryKey: queryKeys.vision.run(runId ?? ""),
		refetchInterval: (query) =>
			query.state.data && isRunOpen(query.state.data) ? RUN_POLL_MS : false,
	});
};

/** A finished run's boxes; they never change once written. */
export const useRunBoxes = (run: undefined | VisionRun) =>
	useQuery({
		enabled: Boolean(run && run.status === VisionRunStatus.Completed),
		queryFn: async () => {
			const response = await apiClient.get<VisionRunBoxes>(
				ENDPOINTS.shadow.vision.boxes(run?.id ?? ""),
			);
			return response.data;
		},
		queryKey: queryKeys.vision.boxes(run?.id ?? ""),
		staleTime: Infinity,
	});

export const useRecipes = () =>
	useQuery({
		queryFn: async () => {
			const response = await apiClient.get<VisionRecipe[]>(
				ENDPOINTS.shadow.vision.recipes,
			);
			return response.data;
		},
		queryKey: queryKeys.vision.recipes,
	});

export const useSaveRecipe = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			id?: string;
			instruction: string;
			name: string;
			plan: VisionPlan;
		}) => {
			const { id, ...body } = input;
			const response = id
				? await apiClient.patch<VisionRecipe>(
						ENDPOINTS.shadow.vision.recipe(id),
						body,
					)
				: await apiClient.post<VisionRecipe>(
						ENDPOINTS.shadow.vision.recipes,
						body,
					);
			return response.data;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: queryKeys.vision.recipes }),
	});
};

export const useDeleteRecipe = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (recipeId: string) => {
			await apiClient.delete(ENDPOINTS.shadow.vision.recipe(recipeId));
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: queryKeys.vision.recipes }),
	});
};
